import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/client';
import { money } from './fees/FeeRuleForm';

// Admin → Accommodation Bookings → booking details (Kayd, 28.10.2026):
// separate statuses (booking · payment · NULA-MOVE fee · refund), the saved
// payment method and cancellation conditions, the refund history, and the
// actions of an authorized admin — cancel with refund preview, decide a
// refund, approve / reject a refund after payout, confirm a cash refund with
// evidence, retry a failed refund, confirm check-in / cash received.
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const day = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const REFUND_LABEL = { under_review: 'Under review', processing: 'Processing', refunded: 'Refunded', partially_refunded: 'Partially refunded', failed: 'Failed', rejected: 'Rejected' };

function Status({ title, st, cur }) {
  if (!st) return null;
  return (
    <div style={s.statusRow}>
      <span style={s.statusTitle}>{title}</span>
      <span style={s.badge}>{st.label}</span>
      <span style={s.muted}>{[st.ref, st.amount ? money(st.amount, cur) : '', st.remaining !== undefined ? `open ${money(st.remaining, cur)}` : '', st.date ? day(st.date) : ''].filter(Boolean).join(' · ')}</span>
    </div>
  );
}

export default function BookingSettlementPanel({ booking: b, onChanged }) {
  const cur = b.price_breakdown?.currency || (b.listing?.country === 'KE' ? 'KES' : 'USD');
  const [mode, setMode] = useState(null); // cancel | refund
  const [preview, setPreview] = useState(null);
  const [amount, setAmount] = useState('');
  const [recovery, setRecovery] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const st = b.settlement || {};
  const terms = b.cancellation_terms;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setMode(null); setNote(''); onChanged(); } catch (e) { toast.error(errText(e, 'Could not save')); } finally { setBusy(false); }
  };
  const ask = (q) => { const r = window.prompt(q); return r && r.trim() ? r.trim() : null; };
  const startCancel = async () => {
    try {
      const r = await api.get(`/api/rooms/admin/bookings/${b.id}/cancel-preview`);
      setPreview(r.data); setAmount(String(r.data.refund_cents / 100)); setMode('cancel');
    } catch (e) { toast.error(errText(e, 'Could not load the refund preview')); }
  };
  const toCents = (v) => (v === '' ? undefined : Math.round(Number(v) * 100));

  const refundAction = (r, action) => {
    if (action === 'approve') {
      const n = ask(`Approve ${r.refund_number}: refund ${money(r.amount_cents, r.currency)}.\nThe provider was already paid out. Reason for the decision (saved in the log):`);
      if (!n) return;
      const rec = window.prompt('Amount to recover from the provider\'s later payouts (in ' + r.currency + ', 0 = none):', '0');
      if (rec === null) return;
      run(() => api.post(`/api/rooms/admin/refunds/${r.id}/approve`, { note: n, recovery_cents: Math.round(Number(rec || 0) * 100) }), 'Refund approved');
    } else if (action === 'reject') {
      const n = ask(`Reject ${r.refund_number}? Reason (saved in the log):`);
      if (n) run(() => api.post(`/api/rooms/admin/refunds/${r.id}/reject`, { note: n }), 'Refund rejected');
    } else if (action === 'cash') {
      const ev = ask(`Confirm that ${money(r.amount_cents, r.currency)} was paid back in cash to the guest.\nEvidence (receipt number, M-Pesa reference, note):`);
      if (ev) run(() => api.post(`/api/rooms/admin/refunds/${r.id}/confirm-cash`, { evidence: ev }), 'Cash refund confirmed');
    } else if (action === 'retry') {
      run(() => api.post(`/api/rooms/admin/refunds/${r.id}/retry`), 'Refund sent again');
    }
  };

  const live = ['confirmed', 'checked_in', 'completed'].includes(b.status);
  return (
    <div style={s.panel}>
      <div style={s.cols}>
        <div style={s.col}>
          <div style={s.h4}>{st.payment_method === 'cash' ? '💵' : '💳'} {st.payment_method_label || 'Online payment'}</div>
          <Status title="Booking" st={st.booking} cur={cur} />
          <Status title="Payment" st={st.payment} cur={cur} />
          <Status title="NULA-MOVE fee" st={st.fee} cur={cur} />
          <Status title="Refund" st={st.refund} cur={cur} />
          <div style={s.muted}>
            Total {money(b.total_price_cents, cur)} · online {money(b.amount_online_cents ?? b.total_price_cents, cur)} · on arrival {money(b.amount_on_arrival_cents || 0, cur)} · NULA-MOVE fee {money(b.platform_fee_cents || 0, cur)}
            {b.checkin_confirmed_at && <> · check-in confirmed {day(b.checkin_confirmed_at)} ({b.checkin_confirmed_role})</>}
            {b.cash_received_at && <> · cash {money(b.cash_received_cents, cur)} confirmed {day(b.cash_received_at)} ({b.cash_received_role})</>}
            {b.payout_done && <> · paid out {money(b.payout_net_cents ?? 0, cur)}{b.payout_offset_cents ? ` after offset ${money(b.payout_offset_cents, cur)}` : ''}</>}
          </div>
        </div>
        <div style={s.col}>
          <div style={s.h4}>Cancellation conditions saved with the booking</div>
          {terms ? (
            <div style={s.muted}>
              <strong>{terms.label}</strong> — {terms.text}
              <ul style={s.ul}>{(terms.deadlines || []).map((d) => <li key={d.until}>{d.refund_percent}% until {day(d.until)}</li>)}</ul>
              Time zone {terms.timezone} · rules version {terms.rules_version}
            </div>
          ) : <div style={s.muted}>Booked before conditions were saved per booking — policy “{b.cancellation_policy || 'standard'}”.</div>}
          {b.cancelled_at && <div style={s.muted}>Cancelled {day(b.cancelled_at)} by {b.cancelled_by_role || b.cancellation_reason}{b.cancellation_note ? ` — ${b.cancellation_note}` : ''}</div>}
        </div>
      </div>

      {(b.refunds || []).length > 0 && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>{['Refund', 'Method', 'Amount', 'Status', 'Basis / reason', 'History', ''].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {b.refunds.map((r) => (
                <tr key={r.id}>
                  <td style={s.td}>{r.refund_number}<div style={s.muted}>{day(r.created_at)}</div></td>
                  <td style={s.td}>{r.method}</td>
                  <td style={s.td}>{money(r.amount_cents, r.currency)}<div style={s.muted}>of {money(r.paid_cents, r.currency)} paid</div></td>
                  <td style={s.td}><span style={s.badge}>{REFUND_LABEL[r.status] || r.status}</span>{r.failure_message && <div style={s.err}>{r.failure_message}</div>}</td>
                  <td style={s.td}><div style={s.muted}>{r.basis}</div><div style={s.muted}>{r.reason}</div>{r.evidence && <div style={s.muted}>Evidence: {r.evidence}</div>}</td>
                  <td style={s.td}>{(r.events || []).map((e, i) => <div key={i} style={s.muted}>{day(e.at)} · {e.type}{e.note ? ` — ${e.note}` : ''}</div>)}</td>
                  <td style={s.tdA}>
                    {r.status === 'under_review' && <><button style={s.link} onClick={() => refundAction(r, 'approve')}>Approve</button><button style={s.linkRed} onClick={() => refundAction(r, 'reject')}>Reject</button></>}
                    {r.status === 'processing' && r.method === 'cash' && <button style={s.link} onClick={() => refundAction(r, 'cash')}>Confirm cash refund</button>}
                    {r.status === 'failed' && !b.refunds.some((x) => x.created_at > r.created_at) && <button style={s.link} onClick={() => refundAction(r, 'retry')}>Try again</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode === 'cancel' && preview ? (
        <div style={s.form}>
          <strong>Cancel booking</strong>
          <div style={s.muted}>{preview.basis}</div>
          <div style={s.muted}>Paid {preview.paid_text} · by the conditions: refund {preview.refund_text}, kept {preview.retained_text}. {preview.effect}</div>
          <label style={s.label}>Refund amount ({cur}) — change only for an approved exception
            <input style={s.input} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label style={s.label}>Reason (required, saved in the log and shown to the guest)
            <textarea style={s.textarea} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div style={s.actions}>
            <button style={s.danger} disabled={busy || !note.trim()} onClick={() => run(() => api.patch(`/api/rooms/admin/bookings/${b.id}/cancel`, { refund_amount_cents: toCents(amount), admin_note: note }), 'Booking cancelled')}>Cancel booking</button>
            <button style={s.btn} onClick={() => setMode(null)}>Back</button>
          </div>
        </div>
      ) : mode === 'refund' ? (
        <div style={s.form}>
          <strong>Refund decided by Nula Move</strong>
          <div style={s.muted}>For a case reviewed by an admin (e.g. a problem reported after the stay started). The booking stays as it is.</div>
          <label style={s.label}>Amount ({cur})
            <input style={s.input} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          {b.payout_done && (
            <label style={s.label}>Recover from the provider's later payouts ({cur}) — the provider was already paid out
              <input style={s.input} type="number" min="0" step="0.01" value={recovery} onChange={(e) => setRecovery(e.target.value)} />
            </label>
          )}
          <label style={s.label}>Reason (required, saved in the log)
            <textarea style={s.textarea} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div style={s.actions}>
            <button style={s.primary} disabled={busy || !note.trim() || !(Number(amount) > 0)} onClick={() => run(() => api.post(`/api/rooms/admin/bookings/${b.id}/refund`, { amount_cents: toCents(amount), recovery_cents: toCents(recovery), reason: note }), 'Refund started')}>Start refund</button>
            <button style={s.btn} onClick={() => setMode(null)}>Back</button>
          </div>
        </div>
      ) : (
        <div style={s.actions}>
          {live && !b.checkin_confirmed_at && <button style={s.btn} disabled={busy} onClick={() => run(() => api.post(`/api/rooms/admin/bookings/${b.id}/confirm-checkin`), 'Check-in confirmed')}>Confirm check-in</button>}
          {live && b.payment_method === 'cash' && !b.cash_received_at && (
            <button style={s.btn} disabled={busy} onClick={() => {
              const v = window.prompt(`Cash received by the provider (${cur}):`, String((b.amount_on_arrival_cents || b.total_price_cents) / 100));
              if (v) run(() => api.post(`/api/rooms/admin/bookings/${b.id}/confirm-cash`, { amount_cents: Math.round(Number(v) * 100) }), 'Cash payment confirmed');
            }}>Confirm cash received</button>
          )}
          {['checked_in', 'completed', 'confirmed'].includes(b.status) && <button style={s.btn} onClick={() => { setAmount(''); setRecovery(''); setMode('refund'); }}>Refund…</button>}
          {['pending', 'confirmed', 'checked_in'].includes(b.status) && <button style={s.dangerLight} onClick={startCancel}>Cancel booking…</button>}
        </div>
      )}
    </div>
  );
}

const s = {
  panel: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  cols: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  col: { flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  h4: { fontSize: 13, fontWeight: 800, color: '#111827' },
  statusRow: { display: 'flex', gap: '4px 8px', flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 },
  statusTitle: { flex: '0 0 100px', color: '#64748b', fontWeight: 700 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#e2e8f0', fontSize: 11, fontWeight: 700, color: '#1e293b' },
  muted: { fontSize: 12, color: '#475569', overflowWrap: 'anywhere' },
  err: { fontSize: 12, color: '#b91c1c' },
  ul: { margin: '4px 0', paddingLeft: 18 },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 },
  table: { width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12.5 },
  th: { textAlign: 'left', padding: '7px 8px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 11.5 },
  td: { padding: '7px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  tdA: { padding: '5px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top', whiteSpace: 'nowrap' },
  form: { background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 560 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#374151' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, maxWidth: 220, boxSizing: 'border-box' },
  textarea: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, minHeight: 56, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btn: { padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  primary: { padding: '7px 12px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 700 },
  danger: { padding: '7px 12px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 700 },
  dangerLight: { padding: '7px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontSize: 13, cursor: 'pointer', fontWeight: 700 },
  link: { background: 'none', border: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontSize: 12.5 },
  linkRed: { background: 'none', border: 'none', color: '#b91c1c', fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontSize: 12.5 },
};
